import Logo from '@/components/icons/Logo';
import { useNavigation } from '@/hooks/useNavigation';
import { useScrollPosition } from '@/hooks/useScrollPosition/useScrollPosition';
import { INavigationMenuItem } from '@/utils/types/navigation';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  VStack,
} from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import React, { useState, useEffect, FC, useRef } from 'react';

const { WWW_ROOT } = require('utils/constants/urls');

const ChevronDownIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width={10}
    height={7}
    viewBox="0 0 10 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.848633 1L4.84863 5L8.84863 1"
      stroke="#545454"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 18L18 6M6 6l12 12"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileMenuIcon: FC<{ isMenuOpen: boolean }> = ({ isMenuOpen }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="24"
    height="24"
  >
    <path
      fillRule="evenodd"
      d="M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75ZM3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z"
      clipRule="evenodd"
    />
  </svg>
);

const Header: FC = () => {
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { menuItems } = useNavigation({ menuType: 'header' });

  const scrollPosition = useScrollPosition();

  useEffect(() => {
    setIsScrolled(scrollPosition > 0);
  }, [scrollPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
    setOpenMenu(null);
  };

  const toggleSubmenu = (index: number) => {
    setOpenMenu((prev) => (prev === index ? null : index));
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSubmenu(index);
    }
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Box
      as="header"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex={1000}
      transition="background-color 0.3s ease"
      bg={isScrolled ? 'white' : 'transparent'}
      boxShadow={isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'}
    >
      <Box maxWidth="1440px" mx="auto" px="1rem">
        <Flex height="84px" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center">
            <Box mr={[0, '64px']}>
              <Link to={WWW_ROOT}>
                <Logo width={117} height={24} alt="Everlywell logo" />
              </Link>
            </Box>

            <Flex
              as="nav"
              display={['none', 'none', 'flex']}
              gap="15px"
              ref={menuRef}
            >
              {menuItems.map((item: INavigationMenuItem, index: number) => (
                <Box key={index} position="relative">
                  <Button
                    variant="ghost"
                    onClick={() => toggleSubmenu(index)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                      handleKeyDown(e, index)
                    }
                    fontWeight="300"
                    fontSize="17px"
                    rightIcon={<ChevronDownIcon />}
                    aria-expanded={openMenu === index}
                    aria-haspopup="true"
                    data-testid={`desktop-menu-button-${item.title
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                  >
                    {item.title}
                  </Button>

                  {openMenu === index && (
                    <Box
                      position="absolute"
                      top="100%"
                      left="0"
                      mt="8px"
                      width="192px"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="4px"
                      boxShadow="md"
                      zIndex={10}
                      data-testid="dropdown-content"
                    >
                      <Box py="4px">
                        {item.submenu.map(
                          (
                            subItem: { title: string; link: string },
                            subIndex: number,
                          ) => (
                            <Link key={subIndex} to={subItem.link}>
                              <Text
                                px="16px"
                                py="16px"
                                color="gray.600"
                                _hover={{ bg: 'gray.50', color: 'black' }}
                              >
                                {subItem.title}
                              </Text>
                            </Link>
                          ),
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Flex>
          </Flex>

          <Link to="/contact-us/">
            <Button
              display={['none', 'none', 'flex']}
              width="130px"
              height="48px"
              fontSize="16px"
              fontWeight="400"
              borderRadius="full"
              backgroundColor="#F9F9F6"
              color="#0F0F0F"
              _hover={{ backgroundColor: '#F0F0F0' }}
            >
              Contact us
            </Button>
          </Link>

          <IconButton
            display={['flex', 'flex', 'none']}
            aria-label="Toggle menu"
            icon={<MobileMenuIcon isMenuOpen={mobileMenuOpen} />}
            onClick={toggleMobileMenu}
            variant="ghost"
            aria-expanded={mobileMenuOpen}
          />
        </Flex>
      </Box>

      {/* Mobile Menu */}
      <Box
        position="fixed"
        top="0"
        left="0"
        width="100%"
        height="100vh"
        bg="white"
        transform={mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)'}
        transition="transform 0.3s"
        zIndex={1000}
        overflowY="auto"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu"
        display={mobileMenuOpen ? 'block' : 'none'}
      >
        <Flex
          px="20px"
          py="20px"
          justifyContent="space-between"
          alignItems="center"
        >
          <Link to={WWW_ROOT}>
            <Logo width={117} height={24} alt="Everlywell logo" />
          </Link>
          <IconButton
            aria-label="Close menu"
            icon={<CloseIcon />}
            onClick={toggleMobileMenu}
            variant="ghost"
          />
        </Flex>

        <VStack spacing="15px" px="20px" mt="12px" align="stretch">
          {menuItems.map((item: INavigationMenuItem, index: number) => (
            <Box key={index} mb="8px">
              <Button
                variant="ghost"
                fontWeight="bold"
                fontSize="18px"
                mb="10px"
                width="100%"
                justifyContent="flex-start"
                onClick={() => toggleSubmenu(index)}
                aria-expanded={openMenu === index}
                data-testid={`mobile-menu-button-${item.title
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                {item.title}
              </Button>
              <VStack as="ul" spacing="8px" pl="16px" align="stretch">
                {item.submenu.map(
                  (
                    subItem: { title: string; link: string },
                    subIndex: number,
                  ) => (
                    <Box as="li" key={subIndex} py="5px">
                      <Link to={subItem.link} onClick={handleLinkClick}>
                        <Text color="gray.700">{subItem.title}</Text>
                      </Link>
                    </Box>
                  ),
                )}
              </VStack>
            </Box>
          ))}
          <Link to="/contact-us/">
            <Button
              width="100%"
              height="48px"
              bg="gray.50"
              borderRadius="full"
              _hover={{ bg: 'gray.100' }}
              mt="12px"
              data-testid="mobile-contact-us-button"
            >
              Contact us
            </Button>
          </Link>
        </VStack>
      </Box>
    </Box>
  );
};

export default Header;
